import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import TooltipWrapper from '@/components/ui/tooltip';
import Share from '@/features/trade/components/share'; // eslint-disable-line import/no-restricted-paths
import { useAccountStore } from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  getAllMarketStoreStates,
} from '@/store/use-markets-store';
import { FilledOrder, useOrdersStore } from '@/store/use-orders-store';
import { OrderStatus } from '@/types';
import { cn } from '@/utils/cn';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  formatBigInt,
} from '@/utils/value-format';

import { getIsOrderOpposite } from '../../utils/helpers';
import { getOrderEntryPrice } from '../../utils/math';

type Props = {
  orderId: string;
};

export default function RealizedPnl({ orderId }: Props) {
  const address = useAccountStore((state) => state.address);
  const order = useOrdersStore(
    useShallow(
      (state) =>
        state.getOrders(address).orders[orderId] as FilledOrder | undefined,
    ),
  );

  const marketSpec = useMemo(() => {
    if (!order?.symbol) return;
    const markets = getAllMarketStoreStates();
    if (!(order.symbol in markets)) return;
    return markets[order.symbol].marketSpec;
  }, [order?.symbol]);

  let entryPrice = 0n;
  if (
    order &&
    (order.status === OrderStatus.FILLED ||
      order.status === OrderStatus.PARTIALLY_FILLED) &&
    order.size > 0n
  ) {
    entryPrice = getOrderEntryPrice(order);
  }

  let roePct: string | undefined;
  if (order && entryPrice > 0n && order.initMarginRatio > 0n) {
    roePct = bigIntToDecimalStr(
      ((order.realizedPnl * 10n ** (COLLATERAL_DECIMALS + 4n)) /
        (order.initMarginRatio * order.size * entryPrice)) *
        100n,
      COLLATERAL_DECIMALS,
    );
  }

  return (
    <div
      className={cn('flex items-center gap-3 text-vestgrey-100', {
        'text-red': order && order.realizedPnl < 0n,
        'text-green': order && order.realizedPnl > 0n,
      })}
    >
      <div>
        {order?.status === OrderStatus.FILLED ||
        order?.status === OrderStatus.PARTIALLY_FILLED
          ? formatBigInt(order.realizedPnl, COLLATERAL_DECIMALS, {
              digits: 2,
              showChange: true,
              style: 'currency',
            })
          : '-'}
      </div>

      {!!marketSpec &&
        (order?.status === OrderStatus.FILLED ||
          order?.status === OrderStatus.PARTIALLY_FILLED) &&
        order.realizedPnl !== order.settledFunding - order.fees && (
          <Share
            onOpen={() => ({
              symbol: order.symbol,
              roePct: roePct ?? '-',
              leverage: formatBigInt(10n ** 6n / order.initMarginRatio, 2n, {
                digits: 0,
                round: 'ceil',
              }),
              isLong: getIsOrderOpposite(order) ? !order.isBuy : order.isBuy,
              pnl: formatBigInt(order.realizedPnl, COLLATERAL_DECIMALS, {
                showChange: true,
                maxDigits: 2,
              }),
              entryPrice,
              lastFilledPrice: order.lastFilledPrice,
              isFilled: true,
            })}
          />
        )}
    </div>
  );
}

export function RealizedPnlHeader() {
  const { t } = useTranslation();
  return (
    <div className="flex w-full max-w-max items-center justify-end md:justify-start">
      <TooltipWrapper
        offset={1.4}
        content={t('ordersHistoryTable.pnlTooltip')}
        className="font-sans text-base normal-case"
        direction="left"
      >
        <span className="md:underline">{t('ordersHistoryTable.pnl')}</span>
      </TooltipWrapper>
    </div>
  );
}

export const realizedPnlAccessorFn = (id: string) => {
  const { address } = useAccountStore.getState();
  const { realizedPnl } = useOrdersStore.getState().getOrders(address).orders[
    id
  ] as FilledOrder;

  const realizedPnlNum = bigIntToNum(realizedPnl, COLLATERAL_DECIMALS);

  return Number.isNaN(realizedPnlNum) ? undefined : realizedPnlNum;
};
